import React, { useEffect, useRef, useState } from "react"

import { isEqual, snakeCase } from "lodash-es"
import { Form, useLoaderData, useRevalidator, useSearchParams, useSubmit } from "react-router-dom"
import { PagyNav } from "~/src/components"
import { Bee } from "~/src/components/BeeKit"
import { SortMenu } from "~/src/components/Catalog/SortMenu"
import { iClient } from "~/src/lib/appClients"
import { Pagy, ProductCatalogItem } from "~/src/serializedRecords"
import { CatalogFilter, FilterValues } from "./CatalogFilter"
import { CatalogFilterToggle } from "./CatalogFilterToggle"
import { CatalogItemGrid } from "./CatalogItemGrid"

//region Types
export type ProductCatalogContentProps = {
  meta: Pagy
  categories: string[]
  productFeatures: string[]
  minPrice: number
  maxPrice: number
  items: ProductCatalogItem[]
}
//endregion

//region Component
export function ProductCatalogContent() {
  //region Hooks
  const data = useLoaderData() as ProductCatalogContentProps
  const revalidator = useRevalidator()
  const [searchParams] = useSearchParams()
  const submit = useSubmit()
  const tabInputRef = useRef<HTMLInputElement>(null)
  const [searchValue, setSearchValue] = useState<string>(searchParams.get("search") ?? "")

  useEffect(() => {
    if (searchValue != searchParams.get("search")) {
      setSearchValue(searchParams.get("search") ?? "")
    }
  }, [searchParams])

  const {
    items,
    meta: { count, next, previous, series = [] },
    categories,
    productFeatures,
    minPrice = 0,
    maxPrice = 1000,
  } = data

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(true)

  const defaultFilterState = {
    categories: [],
    productFeatures: [],
    minimumQuantities: [],
    priceRange: { min: minPrice, max: maxPrice },
  }
  const [selectedFilterValues, setSelectedFilterValues] = useState<FilterValues>(defaultFilterState)

  const formRef = useRef<HTMLFormElement>(null)
  //endregion

  const isFilterDirty = !isEqual(selectedFilterValues, defaultFilterState)

  const sizeTransformer = ({ height, width }: { height: number; width: number }) => {
    const newWidth = width - (isFilterOpen ? CatalogFilter.width : 0)
    return { height, width: newWidth }
  }

  const {
    categories: selectedCategories,
    productFeatures: selectedProductFeatures,
    minimumQuantities: selectedMinimumQuantities,
    priceRange: selectedPriceRange,
  } = selectedFilterValues

  const numSelectedFilters =
    selectedCategories.length +
    selectedProductFeatures.length +
    selectedMinimumQuantities.length +
    (selectedPriceRange.min !== minPrice || selectedPriceRange.max !== maxPrice ? 1 : 0)

  //region Event Handlers
  const handleOnFilterChange = (values: FilterValues) => {
    setSelectedFilterValues(values)
  }

  const handleClearFilters = () => {
    setSelectedFilterValues(defaultFilterState)
  }

  const handleOnApplyFilter = (values: FilterValues) => {
    const {
      categories: selectedCategories,
      productFeatures: selectedProductFeatures,
      minimumQuantities: selectedMinimumQuantities,
      priceRange: selectedPriceRange,
    } = values

    window.alert(
      "Selected Categories: " +
        selectedCategories.join(", ") +
        "\nSelected Product Features: " +
        selectedProductFeatures.join(", ") +
        "\nSelected Minimum Quantities: " +
        selectedMinimumQuantities
          .map((range) => (range.label ? range.label : `[${range.min}-${range.max}]`))
          .join(", ") +
        "\nSelected Price Ranges: " +
        `[${selectedPriceRange.min}, ${selectedPriceRange.max}]`
    )
  }

  const handleLoveProduct = async (item: ProductCatalogItem) => {
    await iClient.put(`/product_preferences/${item.productId}/toggle/love`)

    // Forces a refresh of loader data.
    revalidator.revalidate()
  }

  const handleHideProduct = async (item: ProductCatalogItem) => {
    await iClient.put(`/product_preferences/${item.productId}/toggle/hidden`)

    // Forces a refresh of loader data.
    revalidator.revalidate()
  }

  const handleOnTabClick = (key: string) => () => {
    if (!tabInputRef.current) return

    tabInputRef.current.value = key

    if (!formRef.current) return

    const formData = new FormData(formRef.current)
    formData.delete("search")

    submit(formData)
  }
  //endregion

  //region JSX
  return (
    <div className="flex flex-col mx-6 h-full">
      <Form ref={formRef} method="get" className="contents">
        <Bee.Searchbar
          name="search"
          value={searchValue}
          onChange={(event) => setSearchValue(event.currentTarget.value)}
          onClear={() => {
            setSearchValue("")

            if (!formRef.current) return

            const formData = new FormData(formRef.current)
            formData.delete("search")

            submit(formData)
          }}
          className="w-full mt-8"
        />
        <Bee.HiddenInput name="tab" ref={tabInputRef} defaultValue={searchParams.get("tab") ?? "all_products"} />

        <Bee.TabBar className="mt-2">
          {["All Products", "Favorites", "Hidden", "Past Orders"].map((label) => {
            const key = snakeCase(label)
            const isSelected = (searchParams.get("tab") ?? "all_products") === key

            return (
              <Bee.Tab type="button" key={key} onClick={handleOnTabClick(key)} selected={isSelected}>
                {[label, isSelected && `(${count})`].filter(Boolean).join(" ")}
              </Bee.Tab>
            )
          })}
        </Bee.TabBar>
      </Form>

      <div className="h-full">
        <div className="flex items-center justify-between w-full mt-8">
          <CatalogFilterToggle
            className="pb-2"
            isOpen={isFilterOpen}
            numSelectedFilters={numSelectedFilters}
            onClearAll={() => handleClearFilters()}
            onToggleFilter={() => setIsFilterOpen(!isFilterOpen)}
          />
          <SortMenu />
        </div>
        <div className="flex h-full w-full">
          <CatalogFilter
            disabled={!isFilterDirty}
            isOpen={isFilterOpen}
            numSelectedFilters={numSelectedFilters}
            priceRangeBounds={{ min: minPrice, max: maxPrice }}
            values={selectedFilterValues}
            options={{ categories, productFeatures }}
            onChange={handleOnFilterChange}
            onApply={handleOnApplyFilter}
            onClearAll={handleClearFilters}
            onClose={() => {
              isFilterOpen && setIsFilterOpen(false)
            }}
          />
          <CatalogItemGrid
            sizeTransformer={sizeTransformer}
            onLoveProduct={handleLoveProduct}
            onHideProduct={handleHideProduct}
            items={items}
          />
        </div>
        <div className="flex justify-center p-2">
          <PagyNav previous={previous} next={next} series={series} />
        </div>
      </div>
    </div>
  )
  //endregion
}
//endregion
